import React from 'react';
import MoreMenu from './MoreMenu';

// ---------------------------------------------------------------------------
// Header button emphasis tiers — locked grammar so every entity-header
// button maps to one of two visible tiers (a third tier exists only as
// "lives in the overflow"). Importance of the action drives the tier,
// NOT how many buttons a page happens to have:
//
//   PRIMARY (green filled)  — the single outcome-completing positive
//                             action on the page. At most ONE per header
//                             at any moment. Examples: Brief's
//                             "Complete Brief" (terminal), the editing-
//                             mode "Save Changes". A page with no genuine
//                             terminal action SHOULD HAVE NO primary —
//                             do not manufacture one to fill the cluster.
//
//   STANDARD (teal outline) — standard edit / modify actions. Examples:
//                             Brief's "Amend Brief", Supplier's "Edit
//                             Supplier". The two "edit this record"
//                             actions render identically across entities
//                             because they live at the same emphasis
//                             level — different verbs, same tier.
//
//   OVERFLOW (no class)     — destructive / rare / secondary verbs live
//                             inside MoreMenu via the EntityHeaderActions
//                             `overflowItems` prop. No dedicated button
//                             class — the items list drives appearance
//                             via MoreMenu.
//
// `active: true` inverts the fill ↔ outline for toggle-style buttons
// (Amend / Complete inline panels — pressed reads as "panel open").
//
// Both tiers share one BASE so paddings + radius + transitions never
// drift between them. py-1.5 (24px content + 12px vertical padding)
// is the cluster's compact rhythm — Pin's px-3 py-2 is the deliberate
// exception (icon button needs more visual mass on the leading edge).
//
// Motion system (2026-06 button-motion brief):
//   shared hover/press — colour/background/border transition .16s
//                        ease-in-out, transform .12s. press/active →
//                        scale(.97).
//   focus-visible       — 2px ring offset 2px, tier-coloured.
//   reduced motion      — transition + active-scale disabled under
//                        prefers-reduced-motion: reduce. Buttons still
//                        change colour on hover so the affordance is
//                        intact, just no transforms.
//   icon-nod            — see ICON_NOD_PENCIL / ICON_NOD_PLUS /
//                        ICON_NOD_CHECK below. Driven by the BASE's
//                        `group` class so child icons react to parent
//                        hover.
//
// Pinned toggle is EXCLUDED from this system — it keeps its own
// pin-burst animation rendered by the EntityHeaderActions component
// below. Don't apply the icon-nod classes to a pin star.
const BASE = "rounded-xl font-['Metropolis'] text-sm font-semibold px-4 py-1.5 transition-[background-color,border-color,color,filter,transform] duration-150 ease-in-out active:scale-[0.97] motion-reduce:transition-colors motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#131313] disabled:opacity-50 inline-flex items-center gap-2 border group";

export function headerButtonClass(tier, { active = false } = {}) {
  if (tier === 'primary') {
    // PRIMARY (green filled). Hover lifts brightness rather than
    // shifting the fill — the green stays the colour signal; the
    // hover just nods. Inverted-fill toggle state lands on the
    // outline variant when active=true.
    return active
      ? `${BASE} bg-white border-[#92D400] text-fusion-forest hover:bg-active-green/10 focus-visible:ring-active-green`
      : `${BASE} bg-[#92D400] border-[#92D400] text-[#131313] hover:[filter:brightness(1.08)] focus-visible:ring-white`;
  }
  if (tier === 'standard') {
    // STANDARD (teal outline). Hover brightens border + text to
    // #3FAFC6 and floods a faint teal bg per the motion brief. Active
    // toggle state shows a more saturated bg tint.
    return active
      ? `${BASE} bg-[#2E8FA1]/15 border-[#2E8FA1] text-[#2E8FA1] focus-visible:ring-[#3FAFC6]`
      : `${BASE} bg-white border-[#2E8FA1] text-[#2E8FA1] hover:bg-[#2E8FA1]/[0.14] hover:border-[#3FAFC6] hover:text-[#3FAFC6] focus-visible:ring-[#3FAFC6]`;
  }
  return BASE;
}

// Icon-nod motion classes — applied to the <svg> inside a header
// button. The parent button carries `group` (via BASE above) so the
// icon reacts to PARENT hover, not its own. Each icon's gesture is
// chosen for what the icon MEANS, not by a one-size literal transform:
//
//   PENCIL — tilt (translate + rotate-12°). Reads as "ready to write."
//            Applied to Amend Brief, Amend Supplier, Edit (drafts).
//   PLUS   — scale 1.24. NO rotation: rotating a "+" reads as "×"
//            (cancel), which is the opposite signal. Scale carries
//            the "add" meaning. Applied to + Add Supplier / + Create Brief.
//   CHECK  — scale 1.12 (gentler than plus). Joins the same motion
//            system. Applied to Complete Brief.
//
// All three use the same 280ms spring easing from the mock
// (cubic-bezier(.34,1.56,.64,1)) so the family reads as one motion
// vocabulary. Reduced-motion zeroes the transform entirely.
const ICON_NOD_BASE = "transition-transform duration-[280ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:transition-none";
export const ICON_NOD_PENCIL = `${ICON_NOD_BASE} group-hover:[transform:translate(0.5px,-1px)_rotate(-12deg)] motion-reduce:group-hover:[transform:none]`;
export const ICON_NOD_PLUS = `${ICON_NOD_BASE} group-hover:[transform:scale(1.24)] motion-reduce:group-hover:[transform:none]`;
export const ICON_NOD_CHECK = `${ICON_NOD_BASE} group-hover:[transform:scale(1.12)] motion-reduce:group-hover:[transform:none]`;

// ---------------------------------------------------------------------------
// Header type scale + status pill helpers — applied to BOTH entity-detail
// headers so type sizes, weights, letter-spacing and pill chrome can't
// drift per page. Sourced from one place so any future tweak lands
// everywhere at once.

// Title H1 (green, all-caps). Brief's 24px / extrabold / tracking-0.04em
// pattern wins as the shared spec — Supplier's older 30px / semibold /
// tracking-0.2em felt more spaced and broke for long supplier names.
// Both pages now use this exact class.
export const HEADER_TITLE_CLASS = "font-['Metropolis'] text-[24px] font-extrabold tracking-[0.04em] uppercase text-fusion-forest leading-[1.15] break-words";

// Same title, without the forced caps — for names somebody TYPED rather than
// labels we chose (supplier-detail IA §2). A supplier name is a proper noun:
// uppercasing turns "McKenzie & Sons" into shouting and throws away the casing
// they gave us. Brief titles keep the caps treatment; they're our reference,
// not someone's name.
export const HEADER_TITLE_CLASS_SENTENCE = "font-['Metropolis'] text-[24px] font-extrabold tracking-[0.01em] text-fusion-forest leading-[1.15] break-words";

// Eyebrow / breadcrumb subtitle (Brief: "FS9879 · Kia · …" / Supplier:
// "Category · Region · Status"). One muted micro-label across both
// entity pages.
export const HEADER_EYEBROW_CLASS = "font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-[#51534A]";

// Status pill chrome — one base + a small set of semantic tones so the
// title-adjacent pill row reads as one consistent treatment across
// Brief (state / amended / type) and Supplier (Record Only / Do Not
// Use / On Hold / Risk / Inactive). Brief's compact rounded-full
// chip wins as the shared spec; supplier's older rounded-md text-xs
// medium pills are replaced inline.
//
// All pills share: h-[22px], px-3, text-[10px], extrabold, tracking-
// 0.14em, uppercase, rounded-full. The tone classes layer bg-tint /
// fg / border-tint on top — pick the tone that matches the action's
// semantic meaning, not the eyeballed colour.
export const STATUS_PILL_BASE = "inline-flex items-center gap-1.5 font-['Metropolis'] text-[10px] font-extrabold tracking-[0.14em] uppercase rounded-full h-[22px] px-3 border";

const PILL_TONES = {
  neutral:  'bg-[#51534A]/20 text-fusion-mid-grey border-[#DADCCD]/50',  // muted / inactive / generic
  info:     'bg-[#2E8FA1]/15 text-[#2E8FA1] border-[#2E8FA1]/40',  // info / record-only / awaiting
  warning:  'bg-[#E89A2E]/15 text-[#7A4E10] border-[#E89A2E]/40',  // on-hold / use-with-caution / privileged
  danger:   'bg-[#E0290A]/20 text-[#E0290A] border-[#E0290A]/45',  // do-not-use / high-risk / cancelled
  positive: 'bg-[#92D400]/15 text-fusion-forest border-[#92D400]/40',  // approved / low-risk
};

export function statusPillClass(tone = 'neutral') {
  return `${STATUS_PILL_BASE} ${PILL_TONES[tone] || PILL_TONES.neutral}`;
}
// ---------------------------------------------------------------------------

// Shared entity-header action cluster. Drives the top-right action
// row on every entity-detail page (Brief / Supplier today; future
// pages — Acknowledge audit, Document detail, etc. — compose from
// this too). The component locks the left-to-right grammar so
// alignment can't drift per page:
//
//   [leading icons: comments?, settings cog?] → [error?] → [Pin]
//      → [primary actions] → [overflow ⋮]
//
// Leading icons sit BEFORE Pin (per the 2026-06 leading-icon slot
// decision). The slot handles a variable number of icon affordances
// — Brief passes [comments-count, settings-cog]; Supplier currently
// passes nothing because no supplier-settings panel exists yet. When
// a supplier settings affordance lands it slots in here and the
// cluster reads identically.
//
// Pin is ALWAYS first AFTER the leading icons. Overflow is ALWAYS
// last. Only the middle (the page-specific primary actions passed as
// children) varies between entity types. Destructive verbs (Delete,
// Archive) belong inside the overflow `items` — never as bare buttons
// in the primary row, since a single mis-click on a Delete in the
// main cluster is a real risk on a long-lived entity page.
//
// Settings is exposed as a leading-icon affordance (cog), NOT a
// post-children slot. The brief's auto-archive / mute / auto-reminder
// popover anchors to that cog. Settings is NOT "more actions" — those
// belong in the overflow.
//
// Pin styling matches the SupplierDetail / BriefDetail labelled
// "Pin / Pinned" pill that landed in the earlier entity-header
// alignment pass — same shape, same colour states, same burst-
// animation hook (caller passes starRef + pulsing).
//
// Props:
//   pin            { isPinned, onToggle, label, starRef, pulsing } — required
//   error          optional string — shown between leading icons and pin in red
//   leadingIcons   optional ReactNode — icon affordances BEFORE Pin
//                  (comments-count badge, settings cog, etc.). Variable
//                  length; component renders whatever's passed verbatim.
//   overflowItems  optional array of MoreMenu items — destructive +
//                  secondary actions. Component omits the ⋮ entirely
//                  when this is empty/null.
//   overflowDisabled optional boolean forwarded to MoreMenu
//   children       primary actions, rendered in given order

export default function EntityHeaderActions({
  pin,
  error,
  leadingIcons,
  overflowItems,
  overflowDisabled,
  children,
}) {
  const hasOverflow = Array.isArray(overflowItems) && overflowItems.filter(Boolean).length > 0;
  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      {leadingIcons}
      {error && (
        <p className="font-['Metropolis'] text-sm text-[#E0290A] mr-2">{error}</p>
      )}
      {pin && (
        <button
          type="button"
          onClick={pin.onToggle}
          title={pin.isPinned ? `Unpin ${pin.label || 'item'}` : `Pin ${pin.label || 'item'}`}
          aria-pressed={pin.isPinned}
          className={`rounded-xl font-['Metropolis'] text-sm font-semibold px-3 py-2 border transition-colors inline-flex items-center gap-2 ${
            pin.isPinned
              ? 'bg-[#92D400]/15 border-[#92D400]/40 text-fusion-forest hover:bg-active-green/25'
              : 'bg-white border-line text-fusion-mid-grey hover:border-active-green hover:text-active-green'
          }`}>
          <span ref={pin.starRef} className="relative inline-flex">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill={pin.isPinned ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={pin.pulsing ? 'pin-star--pulsing' : ''}>
              <path d="M12 2 L15 8 L21 9 L17 14 L18 21 L12 18 L6 21 L7 14 L3 9 L9 8 Z" />
            </svg>
          </span>
          {pin.isPinned ? 'Pinned' : 'Pin'}
        </button>
      )}
      {children}
      {hasOverflow && (
        <MoreMenu
          disabled={overflowDisabled}
          items={overflowItems.filter(Boolean)}
        />
      )}
    </div>
  );
}
